import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button } from "@shared/ui/button";
import {
  ChevronRight,
  Plus,
  X,
  HelpCircle,
  RefreshCw,
  DollarSign,
  Package,
} from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { useSopMaterialOptions, useUpdateSopMaterials } from "../../../hooks";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useToast } from "@shared/hooks/use-toast";
import { UpdateSopMaterialsRequest } from "@features/wx/master/types";

// Schema for the materials form
const materialsSchema = z
  .object({
    // Dynamic material fields will be added at runtime
  })
  .catchall(z.any()); // Allow dynamic fields

interface SopMaterialsStepProps {
  state: any;
  onUpdateState: (updates: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  readOnly?: boolean;
  templateId: string;
  onStepSave?: (stepIndex: number, data: any, isDirty: boolean) => void;
}

const SopMaterialsStep: React.FC<SopMaterialsStepProps> = ({
  state,
  onUpdateState,
  onNext,
  onPrevious,
  readOnly = false,
  templateId,
  onStepSave,
}) => {
    const currency = useCurrency();
  // Get operation type from basic info step
  const operationType = state.basicInfo?.operationType || "";

  // Get SOP ID from basic info step (created in previous step)
  const sopId = state.basicInfo?.sopId || templateId;
  const utilityId = useRemoteUtilityId();
  const { toast } = useToast();
  const [isManualAdd, setIsManualAdd] = useState(false);
  const [isManualDelete, setIsManualDelete] = useState(false);

  const [isFormInitialized, setIsFormInitialized] = useState(false);

  // Add the update SOP materials hook
  const updateSopMaterialsMutation = useUpdateSopMaterials();

  // Fetch API materials based on operation type
  const {
    options: apiMaterials,
    isLoading: loadingApiMaterials,
    error: apiMaterialsError,
  } = useSopMaterialOptions(operationType);

  // ✅ FIXED: Create default values for form with proper handling
  const createDefaultValues = useCallback(() => {
    const defaultValues: any = {};

    // Add material field default values
    (state.materials || []).forEach((material: any, index: number) => {
      defaultValues[`material_${index}_name`] = material.name || "";
      defaultValues[`material_${index}_size`] = material.size || "";
      defaultValues[`material_${index}_quantity`] = material.quantity || "";
      defaultValues[`material_${index}_cost`] = material.cost || "";
    });

    return defaultValues;
  }, [state.materials]);

  const materialsForm = useForm<z.infer<typeof materialsSchema>>({
    resolver: zodResolver(materialsSchema),
    defaultValues: createDefaultValues(),
  });

  // ✅ FIXED: Calculate total material cost using both state and form values
  const { totalCost, totalItems } = useMemo(() => {
    const materials = state.materials || [];
    const formValues = materialsForm.getValues();

    let totalCost = 0;
    let totalItems = 0;

    materials.forEach((material: any, index: number) => {
      // Get cost from form first, then fallback to state
      const cost =
        formValues[`material_${index}_cost`] !== undefined
          ? Number(formValues[`material_${index}_cost`]) || 0
          : Number(material.cost) || 0;

      // Get quantity from form first, then fallback to state
      const quantity =
        formValues[`material_${index}_quantity`] !== undefined
          ? Number(formValues[`material_${index}_quantity`]) || 0
          : Number(material.quantity) || 0;

      totalCost += cost * quantity;
      totalItems += quantity;
    });

    return { totalCost, totalItems };
  }, [state.materials, materialsForm.watch()]); // ✅ Watch form changes

  // Track if API materials have been loaded for this operation type
  const [lastLoadedOperationType, setLastLoadedOperationType] = useState("");
  const [apiMaterialsLoaded, setApiMaterialsLoaded] = useState(false);

  // ✅ Enhanced Transform materials data to API format with totals
  const transformMaterialsToApiFormat = useCallback(
    (materials: any[]): UpdateSopMaterialsRequest => {
      // Filter out empty materials and transform to API format
      const transformedMaterials = materials
        .filter((material) => {
          // Only include materials that have at least a name
          return material.name && material.name.trim() !== "";
        })
        .map((material) => {
          const transformedMaterial = {
            name: material.name || "",
            size: material.size || "",
            quantityRequired: material.quantity || "", // ✅ Use camelCase to match TypeScript type
            cost: material.cost || "",
          };

          console.log(
            `📦 Material "${material.name}" transformed:`,
            transformedMaterial
          );
          return transformedMaterial;
        });

      const apiData = {
        material: transformedMaterials,
        materialCostCount: totalCost, // ✅ Use calculated total cost
        remoteUtilityId: parseInt(utilityId || "0"), // ✅ Use camelCase to match TypeScript type
        materialsEquipmentUpdated: true,
      };

      console.log("🚀 Final API payload:", apiData); // Add this for debugging

      return apiData;
    },
    [utilityId, totalCost]
  );

  // Effect to load API materials when operation type changes
  useEffect(() => {
    if (
      operationType &&
      operationType !== lastLoadedOperationType &&
      apiMaterials &&
      apiMaterials.length > 0 &&
      !loadingApiMaterials
    ) {
      setLastLoadedOperationType(operationType);
      setApiMaterialsLoaded(true);
    }
  }, [
    operationType,
    apiMaterials,
    loadingApiMaterials,
    lastLoadedOperationType,
  ]);

  // Group API materials into packages/kits
  const materialPackages = useMemo(() => {
    if (!apiMaterials || apiMaterials.length === 0) return [];

    // Group materials by type or create predefined packages
    const packages = [
      {
        id: "maintenance-kit",
        title: "Asset Maintenance Kit",
        description: "For installation, maintenance, relocation, or disposal",
        materials: apiMaterials
          .filter(
            (m) =>
              m.label?.toLowerCase().includes("bolt") ||
              m.label?.toLowerCase().includes("insulation") ||
              m.label?.toLowerCase().includes("protective")
          )
          .slice(0, 4),
      },
      {
        id: "electrical-kit",
        title: "Electrical Utility Kit",
        description: "Essential tools for electrical utility operations",
        materials: apiMaterials
          .filter(
            (m) =>
              m.label?.toLowerCase().includes("screwdriver") ||
              m.label?.toLowerCase().includes("tester") ||
              m.label?.toLowerCase().includes("wire")
          )
          .slice(0, 4),
      },
      {
        id: "metering-kit",
        title: "Standard Metering Kit",
        description:
          "For meter installation, replacement, inspection, and calibration",
        materials: apiMaterials
          .filter(
            (m) =>
              m.label?.toLowerCase().includes("meter") ||
              m.label?.toLowerCase().includes("cable") ||
              m.label?.toLowerCase().includes("connection")
          )
          .slice(0, 4),
      },
      {
        id: "inspection-kit",
        title: "Field Inspection Tool Set",
        description: "For all types of audits and compliance checks",
        materials: apiMaterials
          .filter(
            (m) =>
              m.label?.toLowerCase().includes("mirror") ||
              m.label?.toLowerCase().includes("flashlight") ||
              m.label?.toLowerCase().includes("tester")
          )
          .slice(0, 4),
      },
    ].filter((pkg) => pkg.materials.length > 0);

    // If no specific matches, create generic packages
    if (packages.length === 0 && apiMaterials.length > 0) {
      const chunkSize = Math.ceil(apiMaterials.length / 3);
      return [
        {
          id: "package-1",
          title: "Standard Material Kit",
          description: "Essential materials for this operation type",
          materials: apiMaterials.slice(0, chunkSize),
        },
        {
          id: "package-2",
          title: "Additional Materials",
          description: "Supplementary materials and supplies",
          materials: apiMaterials.slice(chunkSize, chunkSize * 2),
        },
        {
          id: "package-3",
          title: "Specialty Items",
          description: "Specialized materials for specific requirements",
          materials: apiMaterials.slice(chunkSize * 2),
        },
      ].filter((pkg) => pkg.materials.length > 0);
    }

    return packages;
  }, [apiMaterials]);

  // Initialize materials array if it doesn't exist
  useEffect(() => {
    if (!state.materials) {
      onUpdateState({
        materials: [],
      });
    }
  }, [state.materials, onUpdateState]);

  // ✅ FIXED: Improved form initialization and population
  useEffect(() => {
    if (state.materials) {
      if (isManualAdd || isManualDelete) {
        setIsManualAdd(false); // Reset the flags
        setIsManualDelete(false);
        return; // Don't reset the form
      }
      const defaultValues = createDefaultValues();

      // Always reset form with current state values
      materialsForm.reset(defaultValues);
      setIsFormInitialized(true);

      console.log("🔄 Materials form reset with values:", defaultValues);
    }
  }, [state.materials, createDefaultValues, materialsForm]);

  // ✅ FIXED: Function to handle package selection
  const handlePackageSelect = useCallback(
    (packageData: any) => {
      console.log("🔍 Package selected:", packageData);

      const newMaterials = packageData.materials.map(
        (apiMaterial: any, index: number) => ({
          id: Date.now() + index,
          name: apiMaterial.label || "",
          size: apiMaterial.size || "",
          quantity: apiMaterial.quantity?.toString() || "1",
          cost: apiMaterial.cost?.toString() || "",
          isFromApi: true,
          apiCode: apiMaterial.value,
          packageId: packageData.id,
        })
      );

      console.log("🔍 New materials created:", newMaterials);

      // Add the materials to existing state
      const currentMaterials = state.materials || [];
      const updatedMaterials = [...currentMaterials, ...newMaterials];

      // Update state first
      onUpdateState({
        materials: updatedMaterials,
      });

      // ✅ FIXED: Immediate form value setting without setTimeout
      newMaterials.forEach((material, idx) => {
        const materialIndex = currentMaterials.length + idx;
        const materialFieldValues = {
          [`material_${materialIndex}_name`]: material.name,
          [`material_${materialIndex}_size`]: material.size,
          [`material_${materialIndex}_quantity`]: material.quantity,
          [`material_${materialIndex}_cost`]: material.cost,
        };

        // Set all form values at once
        Object.entries(materialFieldValues).forEach(([key, value]) => {
          materialsForm.setValue(key, value, {
            shouldDirty: true,
            shouldValidate: true,
          });
        });
      });

      console.log("🔍 Materials form values set for package");

      toast({
        title: "Package Added",
        description: `${newMaterials.length} materials from ${packageData.title} have been added.`,
        duration: 2000,
      });
    },
    [state.materials, onUpdateState, materialsForm, toast]
  );

  // Render material package cards
  const renderMaterialPackageCards = () => {
    if (loadingApiMaterials) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="border border-gray-200 rounded-lg p-6 animate-pulse"
            >
              <div className="h-6 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded mb-4"></div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-300 rounded w-3/4"></div>
                <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                <div className="h-3 bg-gray-300 rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (apiMaterialsError) {
      return (
        <div className="p-6 border border-red-200 rounded-lg bg-red-50">
          <p className="text-red-800">
            Error loading material packages. Please try again.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            className="mt-2"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      );
    }

    if (!materialPackages || materialPackages.length === 0) {
      return (
        <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
          <p className="text-gray-600">
            No material packages available for the selected operation type.
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {materialPackages.map((packageData) => (
          <div
            key={packageData.id}
            className="border-2 border-dashed border-blue-300 rounded-lg p-6 hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer bg-white"
            onClick={() => handlePackageSelect(packageData)}
          >
            <div className="mb-4">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                {packageData.title}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {packageData.description}
              </p>
            </div>

            <div className="space-y-2 mb-4">
              {packageData.materials
                .slice(0, 3)
                .map((material: any, index: number) => (
                  <div
                    key={index}
                    className="flex justify-between items-center text-sm"
                  >
                    <span className="text-gray-700 flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      {material.label}
                    </span>
                    <span className="text-gray-500 font-medium">
                      {material.quantity || "1"}
                    </span>
                  </div>
                ))}
              {packageData.materials.length > 3 && (
                <div className="text-xs text-gray-500 mt-1">
                  +{packageData.materials.length - 3} more items
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ✅ Enhanced Form submit with proper form data capture
  const handleFormSubmit = useCallback(
    async (data: any) => {
      // Check if we have a SOP ID
      if (!sopId) {
        toast({
          title: "Error",
          description:
            "SOP ID is missing. Please complete the Basic Information step first.",
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      if (!utilityId) {
        toast({
          title: "Error",
          description: "Utility ID is missing.",
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      try {
        // ✅ Get form values and merge with state materials
        const formValues = materialsForm.getValues();
        const currentMaterials = state.materials || [];

        // ✅ Update materials with current form values
        const updatedMaterials = currentMaterials.map(
          (material: any, index: number) => ({
            ...material,
            name: formValues[`material_${index}_name`] || material.name || "",
            size: formValues[`material_${index}_size`] || material.size || "",
            quantity:
              formValues[`material_${index}_quantity`] ||
              material.quantity ||
              "",
            cost: formValues[`material_${index}_cost`] || material.cost || "",
          })
        );

        if (updatedMaterials.length === 0) {
          toast({
            title: "No Materials",
            description: "Please add at least one material before proceeding.",
            variant: "destructive",
            duration: 5000,
          });
          return;
        }

        // ✅ Check if we have at least one material with a name
        const validMaterials = updatedMaterials.filter(
          (material: any) => material.name && material.name.trim() !== ""
        );

        if (validMaterials.length === 0) {
          toast({
            title: "Invalid Materials",
            description: "Please ensure at least one material has a name.",
            variant: "destructive",
            duration: 5000,
          });
          return;
        }

        // Transform materials to API format
        const apiData = transformMaterialsToApiFormat(updatedMaterials);

        // Call the API to update SOP with materials
        const response = await updateSopMaterialsMutation.mutateAsync({
          sopId: sopId,
          data: apiData,
        });

        // Show success toast
        toast({
          title: "Materials Updated Successfully",
          description: `${validMaterials.length} materials have been added to the SOP.`,
          duration: 3000,
        });

        // Update local state with the updated materials
        onUpdateState({
          materials: updatedMaterials,
        });
        if (onStepSave) {
          await onStepSave(
            3,
            {
              // step index 3 for materials
              materials: updatedMaterials,
            },
            true
          );
        }
        // Proceed to next step
        if (onNext) {
          onNext();
        }
      } catch (error: any) {
        toast({
          title: "Failed to Update Materials",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "An error occurred while updating materials. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      }
    },
    [
      sopId,
      utilityId,
      state.materials,
      materialsForm,
      transformMaterialsToApiFormat,
      updateSopMaterialsMutation,
      onUpdateState,
      onNext,
      toast,
      onStepSave,
    ]
  );

  // Define form fields dynamically
  const allFormFields: FormField[] = useMemo(() => {
    const fields: FormField[] = [];

    // Materials section
    const materials = state.materials || [];
    if (materials.length > 0) {
      materials.forEach((material: any, index: number) => {
        // Material container with border
        fields.push({
          name: `material_${index}_container`,
          label: "",
          type: "text",
          disabled: true,
          value: "",
          customRender: () => (
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-lg text-gray-900">
                  Material {index + 1}
                </h4>
                {material.isFromApi && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    Package Item
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeMaterial(index)}
                disabled={readOnly}
                type="button"
                className="text-gray-400 hover:text-red-500"
              >
                <X size={18} />
              </Button>
            </div>
          ),
          className: "col-span-6",
          classes: {
            container: `w-full bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm ${
              material.isFromApi ? "border-blue-200" : ""
            }`,
          },
          fullWidth: true,
        });

        // Material fields in 2x2 layout
        fields.push(
          {
            name: `material_${index}_name`,
            label: "Material Name",
            type: "text",
            required: true,
            placeholder: "e.g. Digital Meter, Connection Cable",
            value: material.name || "",
            disabled: readOnly || (material.isFromApi && !material.allowEdit),
            onChange: (value: string) => updateMaterial(index, "name", value),
            className: "col-span-3",
            classes: {
              container: "w-full",
              label: "block text-sm font-medium text-gray-900 mb-2",
              input: `w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500 ${
                material.isFromApi ? "bg-blue-50" : ""
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          },
          {
            name: `material_${index}_quantity`,
            label: "Quantity",
            type: "number",
            required: true,
            placeholder: "e.g. 2, 5 pieces, 500ml",
            value: material.quantity || "",
            min: 0, 
            disabled: readOnly,
            onChange: (value: string) =>
              updateMaterial(index, "quantity", value),
            className: "col-span-3",
            classes: {
              container: "w-full",
              label: "block text-sm font-medium text-gray-900 mb-2",
              input: `w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500 ${
                material.isFromApi ? "bg-blue-50" : ""
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          },
          {
            name: `material_${index}_size`,
            label: "Size/Specifications",
            type: "text",
            placeholder: "e.g. 25mm, 2m x 1m, 500ml",
            value: material.size || "",
            disabled: readOnly,
            onChange: (value: string) => updateMaterial(index, "size", value),
            className: "col-span-3",
            classes: {
              container: "w-full",
              label: "block text-sm font-medium text-gray-900 mb-2",
              input: `w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500 ${
                material.isFromApi ? "bg-blue-50" : ""
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          },
          {
            name: `material_${index}_cost`,
            label: `Estimated Cost per Unit (${currency})`,
            type: "text",
            placeholder: `e.g. ${currency} 150, ${currency} 25`,
            value: material.cost || "",
            disabled: readOnly,
            onChange: (value: string) => updateMaterial(index, "cost", value),
            className: "col-span-3",
            classes: {
              container: "w-full",
              label: "block text-sm font-medium text-gray-900 mb-2",
              input: `w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500 ${
                material.isFromApi ? "bg-blue-50" : ""
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          }
        );
      });
    }

    return fields;
  }, [state.materials, readOnly]);

  // ✅ FIXED: Add material with proper form handling
  const addMaterial = useCallback(() => {
    const currentMaterials = state.materials || [];
    const newMaterial = {
      id:
        currentMaterials.length > 0
          ? Math.max(...currentMaterials.map((m: any) => m.id || 0)) + 1
          : 1,
      name: "",
      size: "",
      quantity: "",
      cost: "",
      isFromApi: false,
    };
    setIsManualAdd(true);

    const updatedMaterials = [...currentMaterials, newMaterial];
    onUpdateState({
      materials: updatedMaterials,
    });

    // ✅ Set form values immediately
    const newIndex = updatedMaterials.length - 1;
    const materialFieldValues = {
      [`material_${newIndex}_name`]: "",
      [`material_${newIndex}_size`]: "",
      [`material_${newIndex}_quantity`]: "",
      [`material_${newIndex}_cost`]: "",
    };

    Object.entries(materialFieldValues).forEach(([key, value]) => {
      materialsForm.setValue(key, value, { shouldDirty: true });
    });
  }, [state.materials, onUpdateState, materialsForm]);

  const removeMaterial = useCallback(
    (index: number) => {
      const currentMaterials = state.materials || [];
      const updatedMaterials = [...currentMaterials];
      updatedMaterials.splice(index, 1);
      setIsManualDelete(true);
      onUpdateState({
        materials: updatedMaterials,
      });
    },
    [state.materials, onUpdateState]
  );

  const updateMaterial = useCallback(
    (index: number, field: string, value: string) => {
      const currentMaterials = state.materials || [];
      const updatedMaterials = currentMaterials.map(
        (material: any, i: number) =>
          i === index ? { ...material, [field]: value } : material
      );
      onUpdateState({
        materials: updatedMaterials,
      });

      // Also update the form value immediately to prevent clearing
      materialsForm.setValue(`material_${index}_${field}`, value, {
        shouldDirty: true,
      });
    },
    [state.materials, onUpdateState, materialsForm]
  );

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Handle field changes if needed
  }, []);

  const renderCustomFooter = useCallback(() => {
    if (readOnly) return null;

    const isSubmitting = updateSopMaterialsMutation.isPending;

    return (
      <div className="space-y-4">
        {/* Show warning if no SOP ID */}
        {!sopId && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Warning:</strong> No SOP ID found. Please complete the
              Basic Information step first.
            </p>
          </div>
        )}

        {/* Add Material Button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={addMaterial}
          type="button"
          disabled={isSubmitting}
        >
          <Plus size={16} className="mr-2" /> Add Manual Material
        </Button>

        {/* Navigation */}
        <div className="pt-6 flex justify-between border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onPrevious}
            type="button"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !sopId}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating Materials...
              </>
            ) : (
              <>
                Next <ChevronRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }, [
    readOnly,
    onPrevious,
    addMaterial,
    updateSopMaterialsMutation.isPending,
    sopId,
  ]);

  // ✅ Debug logging for troubleshooting
  useEffect(() => {
    console.log("🔍 Materials Debug Info:", {
      materialsCount: state.materials?.length || 0,
      isFormInitialized,
      formValues: materialsForm.getValues(),
    });
  }, [state.materials, isFormInitialized, materialsForm]);

  return (
    <div className="space-y-6">
      {/* Show loading overlay during API call */}
      {updateSopMaterialsMutation.isPending && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-lg font-medium">
                Updating SOP Materials...
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Show message if no operation type selected */}
      {!operationType && (
        <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
          <p className="text-yellow-800">
            Please select an Operation Type in the Basic Information step to
            load available materials.
          </p>
        </div>
      )}

      {/* Main Content */}
      {/* Always show material package cards at the top */}
      <div>
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Material Packages
              </h2>
              <p className="text-gray-600">
                Select from pre-defined material packages or add individual
                materials
              </p>
            </div>

            {/* ✅ Total Cost and Items Display */}
            {state.materials && state.materials.length > 0 && (
              <div className="flex gap-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <DollarSign className="h-4 w-4 text-green-600" />
                  <div className="text-sm">
                    <span className="font-medium text-green-900">
                      Total Cost:
                    </span>
                    <span className="ml-1 text-green-700">
                      ${totalCost.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                  <Package className="h-4 w-4 text-purple-600" />
                  <div className="text-sm">
                    <span className="font-medium text-purple-900">
                      Total Items:
                    </span>
                    <span className="ml-1 text-purple-700">{totalItems}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {renderMaterialPackageCards()}
      </div>

      {/* Always show the DynamicForm and footer, even if there are no materials */}
      <div className="mt-8">
        {state.materials && state.materials.length > 0 && (
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Materials
          </h2>
        )}
        {isFormInitialized && (
          <DynamicForm
            fields={allFormFields}
            form={materialsForm}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
            config={{
              showSubmitButton: false,
              showResetButton: false,
              classes: {
                form: "space-y-8",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500",
                textarea:
                  "w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500 resize-vertical",
                select:
                  "w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors cursor-pointer bg-white text-gray-900",
                label: "block text-sm font-medium text-gray-900 mb-2",
                error: "text-sm text-red-600 mt-1",
                container: "space-y-2",
              },
            }}
            gridColumns={6}
            responsiveColumns={{ sm: 1, md: 1 }}
            renderCustomFooter={renderCustomFooter}
            className="space-y-8"
          />
        )}
      </div>
    </div>
  );
};

export default SopMaterialsStep;
